import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    HStack,
    Stack,
    Button,
    IconButton,
    useToast
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import ImagePreview from '../../components/ImagePreview';
import { createNewCalendarFocusOfTheDay, getCalendarFocusOfTheDayData, updateCalendarFocusOfTheDayData } from '../../services/CalendarFocusOfTheDay';
import FormLoader from '../../CommonComponents/FormLoader';
import { useSelector } from 'react-redux';
import { handleApiError, showSuccessToast } from '../../util/errorHandler';

const Section1 = () => {
    const { alias } = useParams();
    const [quickSave, setQuickSave] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showImagePreview, setShowImagePreview] = useState(false);
    const [leftFormData, setLeftFormData] = useState({});
    const [rightFormData, setRightFormData] = useState({});
    const [combinedFormData, setCombinedFormData] = useState({});
    const previewEnabled = useSelector(state => state.preview.isEnabled);
    const toast = useToast();
    const navigate = useNavigate();

    // Custom widget for event image with preview icon
    const FocusImageWidget = useCallback((props) => {
        const { value, onChange } = props;
        
        const handleTogglePreview = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowImagePreview(prev => !prev);
        };
        
        return (
            <HStack spacing={2}>
                <Box flex={1}>
                    <input
                        type="text"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="Enter image URL"
                        style={{
                            width: "100%",
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px"
                        }}
                    />
                </Box>
                {value && (
                    <IconButton
                        aria-label={showImagePreview ? "Hide image preview" : "Show image preview"}
                        icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
                        size="sm"
                        colorScheme={showImagePreview ? "red" : "blue"}
                        onClick={handleTogglePreview}
                        title={showImagePreview ? "Close image preview" : "Open image preview"}
                    />
                )}
            </HStack>
        );
    }, [showImagePreview, setShowImagePreview]);

    // Split data for two-column layout
    const splitFormData = (data = {}) => {
        const leftFields = ['_id', 'alias', 'startDate', 'endDate', 'eventImage'];
        const rightFields = ['type', 'isDefault', 'isWeekly', 'isMonthly'];
        
        const leftData = {};
        const rightData = {};
        
        leftFields.forEach(field => {
            if (data.hasOwnProperty(field)) {
                leftData[field] = data[field];
            }
        });
        
        rightFields.forEach(field => {
            if (data.hasOwnProperty(field)) {
                rightData[field] = data[field];
            }
        });
        
        return { leftData, rightData };
    };
    
    // Combine data from two forms
    const combineFormData = (leftData, rightData) => {
        return { ...leftData, ...rightData };
    };

    // Left column schema
    const leftColumnSchema = {
        title: "",
        type: "object",
        properties: {
            _id: { type: "string" },
            alias: { type: "string", title: "Alias", default: "" },
            startDate: { type: "string", format: "date-time", title: "Start Date", default: '' },
            endDate: { type: "string", format: "date-time", title: "End Date", default: '' },
            eventImage: { type: "string", title: "Focus Of The Day Image", default: "" },
        },
        required: ['alias', 'startDate', 'endDate']
    };

    // Right column schema
    const rightColumnSchema = {
        title: "",
        type: "object",
        properties: {
            type: {
                type: "string",
                title: "Type Of Focus Of The Day",
                enum: ['calendar_focus_of_day', 'home_page_banner_1'],
            },
            isDefault: {
                type: "number",
                title: "Is Default Focus Of The Day ?",
                enum: [1, 0],
            },
            isWeekly: {
                type: "number",
                title: "Is Focus Of The Day Weekly?",
                enum: [1, 0],
            },
            isMonthly: {
                type: "number",
                title: "Is Focus Of The Day Monthly?",
                enum: [1, 0],
            },
        }
    };

    // Combined schema for preview mode
    const combinedSchema = {
        title: "Calendar Focus Of The Day Fields",
        type: "object",
        properties: {
            _id: { type: "string" },
            alias: { type: "string", title: "Alias", default: "" },
            startDate: { type: "string", format: "date-time", title: "Start Date", default: '' },
            endDate: { type: "string", format: "date-time", title: "End Date", default: '' },
            eventImage: { type: "string", title: "Focus Of The Day Image", default: "" },
            type: {
                type: "string",
                title: "Type Of Focus Of The Day",
                enum: ['calendar_focus_of_day', 'home_page_banner_1'],
            },
            isDefault: {
                type: "number",
                title: "Is Default Focus Of The Day ?",
                enum: [1, 0],
            },
            isWeekly: {
                type: "number",
                title: "Is Focus Of The Day Weekly?",
                enum: [1, 0],
            },
            isMonthly: {
                type: "number",
                title: "Is Focus Of The Day Monthly?",
                enum: [1, 0],
            },
        },
        required: ['alias', 'startDate', 'endDate']
    };

    // Left column UI schema
    const leftColumnUiSchema = {
        "ui:submitButtonOptions": { "norender": true },
        _id: { "ui:widget": "hidden" },
        alias: { "ui:emptyValue": "", "ui:readonly": !!alias },
        startDate: { "ui:emptyValue": "" },
        endDate: { "ui:emptyValue": "" },
        eventImage: { "ui:widget": "eventImage" },
    };

    // Right column UI schema
    const rightColumnUiSchema = {
        "ui:submitButtonOptions": { "norender": true },
        type: { "ui:emptyValue": "", 'ui:widget': 'select', "ui:enumNames": ['Calendar focus of day', 'Home page banner 1'] },
        isDefault: {
            "ui:widget": "radio",
            "ui:emptyValue": "",
            "ui:enumNames": ["Yes", "No"],
            "ui:options": { inline: true },
            props: {
                display: "flex",
                flexDirection: "row",
                gap: "10px",
            },
        },
        isWeekly: {
            "ui:widget": "radio",
            "ui:emptyValue": "",
            "ui:enumNames": ["Yes", "No"],
            "ui:options": { inline: true },
            props: {
                display: "flex",
                flexDirection: "row",
                gap: "10px",
            },
        },
        isMonthly: {
            "ui:widget": "radio",
            "ui:emptyValue": "",
            "ui:enumNames": ["Yes", "No"],
            "ui:options": { inline: true },
            props: {
                display: "flex",
                flexDirection: "row",
                gap: "10px",
            },
        },
    };

    // Combined UI schema for preview mode
    const combinedUiSchema = {
        "ui:submitButtonOptions": { "norender": true },
        _id: { "ui:widget": "hidden" },
        alias: { "ui:emptyValue": "", "ui:readonly": !!alias },
        startDate: { "ui:emptyValue": "" },
        endDate: { "ui:emptyValue": "" },
        eventImage: { "ui:widget": "eventImage" },
        type: { "ui:emptyValue": "", 'ui:widget': 'select', "ui:enumNames": ['Calendar focus of day', 'Home page banner 1'] },
        isDefault: {
            "ui:widget": "radio",
            "ui:emptyValue": "",
            "ui:enumNames": ["Yes", "No"],
            "ui:options": { inline: true },
            props: {
                display: "flex",
                flexDirection: "row",
                gap: "10px",
            },
        },
        isWeekly: {
            "ui:widget": "radio",
            "ui:emptyValue": "",
            "ui:enumNames": ["Yes", "No"],
            "ui:options": { inline: true },
            props: {
                display: "flex",
                flexDirection: "row",
                gap: "10px",
            },
        },
        isMonthly: {
            "ui:widget": "radio",
            "ui:emptyValue": "",
            "ui:enumNames": ["Yes", "No"],
            "ui:options": { inline: true },
            props: {
                display: "flex",
                flexDirection: "row",
                gap: "10px",
            },
        },
    };

    // Form change handlers
    const handleLeftFormChange = (data) => {
        setLeftFormData(data.formData);
    };

    const handleRightFormChange = (data) => {
        setRightFormData(data.formData);
    };

    const handleCombinedFormChange = (data) => {
        setCombinedFormData(data.formData);
    };

    // Widgets object
    const widgets = useMemo(() => ({
        eventImage: FocusImageWidget
    }), [FocusImageWidget]);

    // Separate useEffect for calendar event data
    useEffect(() => {
        const getBasicData = async () => {
            try {
                if (alias) {
                    const result = await getCalendarFocusOfTheDayData(alias);
                    if (result?.data?.[0]) {
                        const data = result.data[0];
                        
                        // Split data for two-column layout
                        const { leftData, rightData } = splitFormData(data);
                        setLeftFormData(leftData);
                        setRightFormData(rightData);
                        setCombinedFormData(data);
                    }
                } else {
                    // For new records, initialize empty forms
                    const emptyData = {};
                    const { leftData, rightData } = splitFormData(emptyData);
                    setLeftFormData(leftData);
                    setRightFormData(rightData);
                    setCombinedFormData(emptyData);
                }
            } catch (error) {
                handleApiError(error, toast, {
                    defaultErrorMessage: "Failed to fetch calendar focus of the day data"
                });
            } finally {
                setIsLoading(false);
            }
        };

        getBasicData();
    }, [alias, quickSave, toast]);



    const handleSubmit = async () => {
        setIsSubmitting(true);
        
        try {
            let finalFormData;
            
            if (previewEnabled) {
                finalFormData = combinedFormData;
            } else {
                finalFormData = combineFormData(leftFormData, rightFormData);
            }
            
            // Remove _id from create operations
            if (!alias && finalFormData._id) {
                delete finalFormData._id;
            }
            
            if (alias) {
                const result = await updateCalendarFocusOfTheDayData(alias, finalFormData);
                if (result?.status === 200) {
                    setQuickSave(prev => !prev);
                    showSuccessToast(toast, "Data updated successfully");
                }
            } else {
                const result = await createNewCalendarFocusOfTheDay(finalFormData);
                if (result?.status === 201) {
                    setQuickSave(prev => !prev);
                    showSuccessToast(toast, "New calendar focus of the day created successfully");
                    navigate(`/calendar/focus-of-the-day/${result?.data?.alias}`);
                }
            }
        } catch (error) {
            handleApiError(error, toast);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box maxW="auto" pb="100px">
            {isLoading ? (
                <FormLoader />
            ) : (
                <Box>
                    {previewEnabled ? (
                        /* Single column layout for preview mode */
                        <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
                            <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                                <Box fontSize="lg" fontWeight="bold" mb={4}>Focus Of The Day Fields</Box>
                                <Form
                                    schema={combinedSchema}
                                    uiSchema={combinedUiSchema}
                                    formData={combinedFormData}
                                    validator={validator}
                                    onChange={handleCombinedFormChange}
                                    widgets={widgets}
                                    fontsize={"14px"}
                                />
                            </Box>
                            {/* Third column for image preview when clicked */}
                            {showImagePreview && combinedFormData?.eventImage && (
                                <HStack sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}>
                                    <ImagePreview image={combinedFormData?.eventImage} />
                                </HStack>
                            )}
                        </Box>
                    ) : (
                        /* Two column layout with separate forms */
                        <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
                            <Stack flexDirection="row" gap={4} alignItems="flex-start" flex={1}>
                                <HStack alignItems="flex-start" flex={1}>
                                    {/* Left Column */}
                                    <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                                        <Box fontSize="lg" fontWeight="bold" mb={4}>Focus Of The Day Fields</Box>
                                        <Form
                                            schema={leftColumnSchema}
                                            uiSchema={leftColumnUiSchema}
                                            formData={leftFormData}
                                            validator={validator}
                                            onChange={handleLeftFormChange}
                                            widgets={widgets}
                                            fontsize={"14px"}
                                        />
                                    </Box>
                                    {/* Right Column */}
                                    <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                                        <Form
                                            schema={rightColumnSchema}
                                            uiSchema={rightColumnUiSchema}
                                            formData={rightFormData}
                                            validator={validator}
                                            onChange={handleRightFormChange}
                                            fontsize={"14px"}
                                        />
                                    </Box>
                                </HStack>
                            </Stack>
                            {/* Third column for image preview when clicked */}
                            {showImagePreview && leftFormData?.eventImage && (
                                <HStack sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}>
                                    <ImagePreview image={leftFormData?.eventImage} />
                                </HStack>
                            )}
                        </Box>
                    )}
                </Box>
            )}
            
            {/* Fixed Save Button */}
            <Box
                position="fixed"
                bottom={0}
                left={"250px"}
                right={0}
                bg="white"
                borderTop="1px solid"
                borderColor="gray.200"
                p={4}
                zIndex={1000}
            >
                <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
                    <Button
                        colorScheme="blue"
                        onClick={handleSubmit}
                        isLoading={isSubmitting}
                        width="auto"
                        margin="0 auto"
                        textAlign="center"
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default Section1;